<template>
  <subscription-guard>
    <div class="pulse-dashboard">

      <!-- Header -->
      <div class="dash-header">
        <div>
          <h1 class="dash-title">{{ greeting }}, {{ firstName }} 👋</h1>
          <p class="dash-sub">A quick overview of your data.</p>
        </div>
        <div class="seg-ctrl">
          <button
            v-for="p in periods"
            :key="p.key"
            class="seg-btn"
            :class="{ active: activePeriod === p.key }"
            @click="setPeriod(p.key)"
          >
            <svg v-if="p.icon === 'calendar'" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:3px;vertical-align:middle"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            {{ p.label }}
          </button>
        </div>
      </div>

      <!-- Custom date range dropdown -->
      <div v-if="activePeriod === 'custom' && customOpen" class="custom-range-drop">
        <span class="crd-label">FROM</span>
        <input type="date" class="crd-input" v-model="customStart" />
        <svg class="crd-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="color:var(--ink-3)"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        <span class="crd-label">TO</span>
        <input type="date" class="crd-input" v-model="customEnd" />
        <button class="btn btn-primary btn-sm" style="height:34px" @click="applyCustom">Apply</button>
      </div>

      <!-- KPI grid -->
      <div class="kpi-grid" v-if="!isLoading">

        <!-- Revenue — large card -->
        <div class="kpi-card kpi-card--large">
          <div class="kpi-head">
            <span class="kpi-dot" style="background:#F7625B"></span>
            <span class="kpi-lbl">REVENUE</span>
            <span class="kpi-delta" :class="deltaClass(total.revenue, prev.revenue)">
              <svg v-if="deltaVal(total.revenue, prev.revenue) >= 0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
              {{ deltaStr(total.revenue, prev.revenue) }}
            </span>
          </div>
          <div class="kpi-val">
            {{ fmt(total.revenue) }} <span class="kpi-unit">kr</span>
          </div>
          <div class="kpi-sub" v-if="total.roi > 0">
            Return on spend {{ total.roi.toFixed(1) }}× · {{ total.sales }} sales
          </div>
          <div class="kpi-sparkline spark-wrap"
               @mousemove="onSparkHover($event, 'revenue')"
               @mouseleave="tooltip = null">
            <svg viewBox="0 0 200 52" preserveAspectRatio="none">
              <defs>
                <linearGradient id="g-rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#F7625B" stop-opacity=".18"/>
                  <stop offset="100%" stop-color="#F7625B" stop-opacity="0"/>
                </linearGradient>
              </defs>
              <path :d="areaPath(series.revenue, 200, 48)" fill="url(#g-rev)" />
              <path :d="linePath(series.revenue, 200, 48)" fill="none" stroke="#F7625B" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" />
              <line v-if="tooltip && tooltip.key === 'revenue'"
                :x1="(tooltip.idx / (series.revenue.length - 1)) * 200"
                :x2="(tooltip.idx / (series.revenue.length - 1)) * 200"
                y1="0" y2="52" stroke="#F7625B" stroke-width="1" stroke-dasharray="3 2" opacity=".5" />
            </svg>
            <div v-if="tooltip && tooltip.key === 'revenue'" class="spark-tip" :style="{ left: tooltip.px + 'px' }">
              <span class="tip-date">{{ tooltip.date ? $moment(tooltip.date).format('D MMM') : '' }}</span>
              <span class="tip-val">{{ fmt(tooltip.value) }} kr</span>
            </div>
          </div>
        </div>

        <!-- Clicks -->
        <div class="kpi-card">
          <div class="kpi-head">
            <span class="kpi-dot" style="background:#4F9CF9"></span>
            <span class="kpi-lbl">CLICKS</span>
            <span class="kpi-delta" :class="deltaClass(total.clicks, prev.clicks)">
              <svg v-if="deltaVal(total.clicks, prev.clicks) >= 0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
              {{ deltaStr(total.clicks, prev.clicks) }}
            </span>
          </div>
          <div class="kpi-val">{{ fmtN(total.clicks) }}</div>
          <div class="kpi-sparkline spark-wrap"
               @mousemove="onSparkHover($event, 'clicks')"
               @mouseleave="tooltip = null">
            <svg viewBox="0 0 200 52" preserveAspectRatio="none">
              <defs>
                <linearGradient id="g-clk" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#4F9CF9" stop-opacity=".18"/>
                  <stop offset="100%" stop-color="#4F9CF9" stop-opacity="0"/>
                </linearGradient>
              </defs>
              <path :d="areaPath(series.clicks, 200, 48)" fill="url(#g-clk)" />
              <path :d="linePath(series.clicks, 200, 48)" fill="none" stroke="#4F9CF9" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" />
              <line v-if="tooltip && tooltip.key === 'clicks'"
                :x1="(tooltip.idx / (series.clicks.length - 1)) * 200"
                :x2="(tooltip.idx / (series.clicks.length - 1)) * 200"
                y1="0" y2="52" stroke="#4F9CF9" stroke-width="1" stroke-dasharray="3 2" opacity=".5" />
            </svg>
            <div v-if="tooltip && tooltip.key === 'clicks'" class="spark-tip" :style="{ left: tooltip.px + 'px' }">
              <span class="tip-date">{{ tooltip.date ? $moment(tooltip.date).format('D MMM') : '' }}</span>
              <span class="tip-val">{{ fmtN(tooltip.value) }}</span>
            </div>
          </div>
        </div>

        <!-- Unique Clicks -->
        <div class="kpi-card">
          <div class="kpi-head">
            <span class="kpi-dot" style="background:#9B8FE8"></span>
            <span class="kpi-lbl">UNIQUE CLICKS</span>
            <span class="kpi-delta" :class="deltaClass(total.unique_clicks, prev.unique_clicks)">
              <svg v-if="deltaVal(total.unique_clicks, prev.unique_clicks) >= 0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
              {{ deltaStr(total.unique_clicks, prev.unique_clicks) }}
            </span>
          </div>
          <div class="kpi-val">{{ fmtN(total.unique_clicks) }}</div>
          <div class="kpi-sub" v-if="total.cpc > 0">CPC {{ fmt(total.cpc) }} kr</div>
          <div class="kpi-sparkline spark-wrap"
               @mousemove="onSparkHover($event, 'unique_clicks')"
               @mouseleave="tooltip = null">
            <svg viewBox="0 0 200 52" preserveAspectRatio="none">
              <defs>
                <linearGradient id="g-uclk" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#9B8FE8" stop-opacity=".18"/>
                  <stop offset="100%" stop-color="#9B8FE8" stop-opacity="0"/>
                </linearGradient>
              </defs>
              <path :d="areaPath(series.unique_clicks, 200, 48)" fill="url(#g-uclk)" />
              <path :d="linePath(series.unique_clicks, 200, 48)" fill="none" stroke="#9B8FE8" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" />
              <line v-if="tooltip && tooltip.key === 'unique_clicks'"
                :x1="(tooltip.idx / (series.unique_clicks.length - 1)) * 200"
                :x2="(tooltip.idx / (series.unique_clicks.length - 1)) * 200"
                y1="0" y2="52" stroke="#9B8FE8" stroke-width="1" stroke-dasharray="3 2" opacity=".5" />
            </svg>
            <div v-if="tooltip && tooltip.key === 'unique_clicks'" class="spark-tip" :style="{ left: tooltip.px + 'px' }">
              <span class="tip-date">{{ tooltip.date ? $moment(tooltip.date).format('D MMM') : '' }}</span>
              <span class="tip-val">{{ fmtN(tooltip.value) }}</span>
            </div>
          </div>
        </div>

        <!-- Sales -->
        <div class="kpi-card">
          <div class="kpi-head">
            <span class="kpi-dot" style="background:#2BE38B"></span>
            <span class="kpi-lbl">SALES</span>
            <span class="kpi-delta" :class="deltaClass(total.sales, prev.sales)">
              <svg v-if="deltaVal(total.sales, prev.sales) >= 0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
              {{ deltaStr(total.sales, prev.sales) }}
            </span>
          </div>
          <div class="kpi-val">{{ fmtN(total.sales) }}</div>
          <div class="kpi-sub" v-if="total.conversion > 0">Conversion {{ total.conversion }}%</div>
          <div class="kpi-sparkline spark-wrap"
               @mousemove="onSparkHover($event, 'sales')"
               @mouseleave="tooltip = null">
            <svg viewBox="0 0 200 52" preserveAspectRatio="none">
              <defs>
                <linearGradient id="g-sal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#2BE38B" stop-opacity=".18"/>
                  <stop offset="100%" stop-color="#2BE38B" stop-opacity="0"/>
                </linearGradient>
              </defs>
              <path :d="areaPath(series.sales, 200, 48)" fill="url(#g-sal)" />
              <path :d="linePath(series.sales, 200, 48)" fill="none" stroke="#2BE38B" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" />
              <line v-if="tooltip && tooltip.key === 'sales'"
                :x1="(tooltip.idx / (series.sales.length - 1)) * 200"
                :x2="(tooltip.idx / (series.sales.length - 1)) * 200"
                y1="0" y2="52" stroke="#2BE38B" stroke-width="1" stroke-dasharray="3 2" opacity=".5" />
            </svg>
            <div v-if="tooltip && tooltip.key === 'sales'" class="spark-tip" :style="{ left: tooltip.px + 'px' }">
              <span class="tip-date">{{ tooltip.date ? $moment(tooltip.date).format('D MMM') : '' }}</span>
              <span class="tip-val">{{ fmtN(tooltip.value) }}</span>
            </div>
          </div>
        </div>

        <!-- Commission -->
        <div class="kpi-card">
          <div class="kpi-head">
            <span class="kpi-dot" style="background:#F4A535"></span>
            <span class="kpi-lbl">COMMISSION</span>
            <span class="kpi-delta" :class="deltaClass(total.commission, prev.commission)">
              <svg v-if="deltaVal(total.commission, prev.commission) >= 0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
              {{ deltaStr(total.commission, prev.commission) }}
            </span>
          </div>
          <div class="kpi-val">{{ fmt(total.commission) }} <span class="kpi-unit">kr</span></div>
          <div class="kpi-sparkline spark-wrap"
               @mousemove="onSparkHover($event, 'commission')"
               @mouseleave="tooltip = null">
            <svg viewBox="0 0 200 52" preserveAspectRatio="none">
              <defs>
                <linearGradient id="g-com" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#F4A535" stop-opacity=".18"/>
                  <stop offset="100%" stop-color="#F4A535" stop-opacity="0"/>
                </linearGradient>
              </defs>
              <path :d="areaPath(series.commission, 200, 48)" fill="url(#g-com)" />
              <path :d="linePath(series.commission, 200, 48)" fill="none" stroke="#F4A535" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" />
              <line v-if="tooltip && tooltip.key === 'commission'"
                :x1="(tooltip.idx / (series.commission.length - 1)) * 200"
                :x2="(tooltip.idx / (series.commission.length - 1)) * 200"
                y1="0" y2="52" stroke="#F4A535" stroke-width="1" stroke-dasharray="3 2" opacity=".5" />
            </svg>
            <div v-if="tooltip && tooltip.key === 'commission'" class="spark-tip" :style="{ left: tooltip.px + 'px' }">
              <span class="tip-date">{{ tooltip.date ? $moment(tooltip.date).format('D MMM') : '' }}</span>
              <span class="tip-val">{{ fmt(tooltip.value) }} kr</span>
            </div>
          </div>
        </div>

      </div>

      <!-- Loading skeleton -->
      <div class="kpi-grid" v-else>
        <div class="kpi-card kpi-card--large" style="min-height:260px;background:var(--surface-3);border-color:transparent;animation:pulse 1.4s ease infinite"></div>
        <div class="kpi-card" v-for="i in 4" :key="i" style="min-height:120px;background:var(--surface-3);border-color:transparent;animation:pulse 1.4s ease infinite"></div>
      </div>

      <!-- Notices -->
      <template v-if="noticesLoading || notices.length">
        <div class="notices-header">
          <span class="notices-title">Recommendations</span>
        </div>

        <div v-if="noticesLoading" class="notices-list">
          <div v-for="i in 3" :key="i" class="notice-card" style="min-height:72px;background:var(--surface-3);border-color:transparent;animation:pulse 1.4s ease infinite"></div>
        </div>

        <div v-else class="notices-list">
          <div
            v-for="notice in topNotices"
            :key="'n-' + notice.rank"
            class="notice-card"
            :class="noticeClass(notice.rank)"
          >
            <div class="notice-badge" :class="{ 'notice-badge--top': notice.rank === 1 }" :style="notice.rank > 1 ? { color: noticeColor(notice.rank), borderColor: noticeColor(notice.rank) } : {}">{{ notice.rank }}</div>
            <div class="notice-body">
              <p class="notice-desc" v-html="notice.html"></p>
            </div>
          </div>

          <div
            v-for="notice in lowNotices"
            :key="'n-' + notice.rank"
            class="notice-card notice-card--low"
          >
            <div class="notice-badge" :style="{ color: noticeColor(notice.rank), borderColor: noticeColor(notice.rank) }">{{ notice.rank }}</div>
            <div class="notice-body">
              <p class="notice-desc" v-html="notice.html"></p>
            </div>
          </div>
        </div>
      </template>

    </div>
  </subscription-guard>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import SubscriptionGuard from '@/components/business/pages/SubscriptionGuard';

export default {
  name: 'BusinessDashboard',
  components: { SubscriptionGuard },

  data() {
    return {
      isLoading: true,
      activePeriod: 'month',
      customOpen: false,
      customStart: this.$moment().startOf('month').format('YYYY-MM-DD'),
      customEnd:   this.$moment().format('YYYY-MM-DD'),
      dailyStats:  [],
      tooltip: null,
      prevStats:   [],
      notices: [],
      noticesLoading: false,
      periods: [
        { key: 'today',  label: 'Today' },
        { key: 'month',  label: 'This month' },
        { key: 'monthly',label: 'Monthly' },
        { key: 'custom', label: 'Custom', icon: 'calendar' },
      ],
    };
  },

  computed: {
    ...mapState('core/auth', ['user']),
    ...mapGetters('core/auth', ['isBusiness', 'isAgency', 'isModerator', 'isInfluencer']),
    ...mapGetters('subscriptions', ['hasSubscription']),

    business() {
      return this.user?.business;
    },
    firstName() {
      return this.user?.first_name || this.user?.business?.name || 'there';
    },
    greeting() {
      const h = new Date().getHours();
      if (h < 12) return 'Good morning';
      if (h < 18) return 'Good afternoon';
      return 'Good evening';
    },

    topNotices() { return this.notices.filter(n => n.rank <= 3); },
    lowNotices()  { return this.notices.filter(n => n.rank > 3); },

    series() {
      const out = { clicks: [], unique_clicks: [], sales: [], revenue: [], commission: [] };
      this.dailyStats.forEach(item => {
        out.clicks.push(+item.clicks);
        out.unique_clicks.push(+item.unique_clicks);
        out.sales.push(+item.sales);
        out.revenue.push(+item.value);
        out.commission.push(+item.commission);
      });
      return out;
    },

    total() {
      const t = { clicks: 0, unique_clicks: 0, sales: 0, revenue: 0, commission: 0, conversion: 0, roi: 0, cpc: 0 };
      this.dailyStats.forEach(d => {
        t.clicks        += +d.clicks;
        t.unique_clicks += +d.unique_clicks;
        t.sales         += +d.sales;
        t.revenue       += +d.value;
        t.commission    += +d.commission;
      });
      t.revenue    = Math.round(t.revenue    * 100) / 100;
      t.commission = Math.round(t.commission * 100) / 100;
      if (t.sales && t.unique_clicks) t.conversion = Math.round((t.sales / t.unique_clicks) * 10000) / 100;
      if (t.commission && t.unique_clicks) t.cpc = t.commission / t.unique_clicks;
      if (t.revenue && t.commission) t.roi = (t.revenue - t.commission) / t.commission;
      return t;
    },

    prev() {
      const t = { clicks: 0, unique_clicks: 0, sales: 0, revenue: 0, commission: 0 };
      this.prevStats.forEach(d => {
        t.clicks        += +d.clicks;
        t.unique_clicks += +d.unique_clicks;
        t.sales         += +d.sales;
        t.revenue       += +d.value;
        t.commission    += +d.commission;
      });
      return t;
    },
  },
  watch: {
    hasSubscription: {
      immediate: true,
      async handler(hasSubscription) {
        if (!hasSubscription || !this.business) return;
        const isNew = (new Date() - new Date(this.business.created_at)) < 7 * 24 * 60 * 60 * 1000;
        if (!isNew) return;

        let hasNoState = true;
        try {
          const res = await this.$gql.query('successGuideState', { result: '' });
          hasNoState = !res.data.successGuideState;
        } catch (e) {
          hasNoState = !localStorage.getItem(`successGuideState_${this.business.uuid}`);
        }

        if (hasNoState) {
          this.$router.push({ name: 'business.success-guide', query: { autostart: 'true' } });
        }
      },
    },
  },

  methods: {
    ...mapActions('core', ['getStats']),

    dateRange() {
      const m = this.$moment;
      switch (this.activePeriod) {
        case 'today':
          return { start: m().format('YYYY-MM-DD'), end: m().format('YYYY-MM-DD') };
        case 'month':
          return { start: m().startOf('month').format('YYYY-MM-DD'), end: m().format('YYYY-MM-DD') };
        case 'monthly':
          return { start: m().startOf('year').format('YYYY-MM-DD'), end: m().format('YYYY-MM-DD') };
        case 'custom':
          return { start: this.customStart, end: this.customEnd };
        default:
          return { start: m().startOf('month').format('YYYY-MM-DD'), end: m().format('YYYY-MM-DD') };
      }
    },

    prevRange() {
      const m = this.$moment;
      switch (this.activePeriod) {
        case 'today':
          return { start: m().subtract(1, 'day').format('YYYY-MM-DD'), end: m().subtract(1, 'day').format('YYYY-MM-DD') };
        case 'month':
          return {
            start: m().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
            end:   m().subtract(1, 'month').format('YYYY-MM-DD'),
          };
        case 'monthly':
          return { start: m().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'), end: m().subtract(1, 'year').format('YYYY-MM-DD') };
        case 'custom': {
          const days = m(this.customEnd).diff(m(this.customStart), 'days') + 1;
          return {
            start: m(this.customStart).subtract(days, 'days').format('YYYY-MM-DD'),
            end:   m(this.customStart).subtract(1, 'day').format('YYYY-MM-DD'),
          };
        }
        default:
          return { start: m().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), end: m().subtract(1, 'month').format('YYYY-MM-DD') };
      }
    },

    fetchAll() {
      this.isLoading = true;
      const { start, end } = this.dateRange();
      const pr = this.prevRange();
      this.getStats({ start_date: start, end_date: end, group: ['date'] }).then(
        cur => { this.dailyStats = cur || []; },
        err => { console.error('stats error:', err); }
      );
      this.getStats({ start_date: pr.start, end_date: pr.end, group: ['date'] }).then(
        prev => { this.prevStats = prev || []; this.isLoading = false; },
        () => { this.isLoading = false; }
      );
    },

    setPeriod(key) {
      this.activePeriod = key;
      if (key === 'custom') {
        this.customOpen = true;
      } else {
        this.customOpen = false;
        this.fetchAll();
      }
    },

    applyCustom() {
      this.customOpen = false;
      this.fetchAll();
    },

    fetchNotices() {
      this.noticesLoading = true;
      this.$gql.query('businessNotices', {
        result: '{ rank key priority html text }',
      }).then(
        res => { console.log('notices:', JSON.stringify(res.data.businessNotices, null, 2)); this.notices = res.data.businessNotices || []; this.noticesLoading = false; },
        () => { this.noticesLoading = false; }
      );
    },

    noticeColor(rank) {
      if (rank === 1) return '#1db87a';
      return '#6366f1';
    },
    noticeClass(rank) {
      if (rank === 1) return 'notice-card--top';
      if (rank <= 3) return 'notice-card--mid';
      return 'notice-card--low';
    },

    fmt(v)  { return new Intl.NumberFormat('da-DK', { maximumFractionDigits: 2 }).format(v || 0); },
    fmtN(v) { return new Intl.NumberFormat('da-DK').format(v || 0); },

    deltaVal(cur, prv) { return prv ? ((cur - prv) / prv) * 100 : 0; },
    deltaStr(cur, prv) {
      const d = this.deltaVal(cur, prv);
      return (d >= 0 ? '+' : '') + d.toFixed(1) + '%';
    },
    deltaClass(cur, prv) { return this.deltaVal(cur, prv) >= 0 ? 'pos' : 'neg'; },

    linePath(values, w, h) {
      if (!values || values.length < 2) return '';
      const min = Math.min(...values);
      const max = Math.max(...values);
      const rng = max - min || 1;
      const pts = values.map((v, i) => {
        const x = (i / (values.length - 1)) * w;
        const y = h - ((v - min) / rng) * (h - 4) - 2;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      });
      return 'M ' + pts.join(' L ');
    },
    areaPath(values, w, h) {
      if (!values || values.length < 2) return '';
      const line = this.linePath(values, w, h);
      return `${line} L ${w},${h} L 0,${h} Z`;
    },

    onSparkHover(e, key) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const values = this.series[key];
      if (!values || values.length < 2) return;
      const idx = Math.min(
        values.length - 1,
        Math.max(0, Math.round((x / rect.width) * (values.length - 1)))
      );
      const date = this.dailyStats[idx] ? this.dailyStats[idx].date : null;
      const value = values[idx];
      this.tooltip = { key, idx, value, date, px: x };
    },
  },

  mounted() {
    this.fetchAll();
    this.fetchNotices();
  },
};
</script>

<style scoped>
.pulse-dashboard {
  position: relative;
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 24px 20px 40px;
}

/* Header */
.dash-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.dash-title {
  font-size: 22px;
  font-weight: 650;
  color: var(--ink);
  margin: 0 0 2px;
  letter-spacing: -.02em;
}
.dash-sub {
  font-size: 13.5px;
  color: var(--ink-3);
  margin: 0;
}

/* Segment control */
.seg-ctrl {
  display: inline-flex;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-ctrl);
  padding: 3px;
  gap: 2px;
}
.seg-btn {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-3);
  transition: background .14s, color .14s;
  white-space: nowrap;
}
.seg-btn:hover  { color: var(--ink); }
.seg-btn.active { background: var(--v-secondary-base); color: var(--ink); font-weight: 600; }

/* Custom date range row */
.custom-range-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

/* Custom date range dropdown */
.custom-range-drop {
  display: flex;
  align-items: center;
  gap: 8px;
  position: absolute;
  right: 20px;
  top: 72px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  box-shadow: var(--shadow-md);
  padding: 12px 14px;
  z-index: 50;
}
.crd-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .08em;
  color: var(--ink-3);
  text-transform: uppercase;
}
.crd-input {
  height: 34px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: var(--r-ctrl);
  font-size: 13px;
  color: var(--ink);
  background: var(--surface);
  outline: none;
}
.crd-input:focus { border-color: var(--accent); }

/* KPI grid */
.kpi-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 12px;
}
.kpi-card--large {
  grid-row: span 2;
}

/* KPI card */
.kpi-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  padding: 20px 20px 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Card head row */
.kpi-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}
.kpi-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
}
.kpi-lbl {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: .08em;
  color: var(--ink-3);
  text-transform: uppercase;
  flex: 1;
}
.kpi-delta {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  font-weight: 600;
}
.kpi-delta.pos { color: #1db87a; }
.kpi-delta.neg { color: #e03e38; }

/* Value */
.kpi-val {
  font-size: 28px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -.03em;
  line-height: 1.1;
  margin-bottom: 4px;
}
.kpi-card--large .kpi-val {
  font-size: 38px;
}
.kpi-unit {
  font-size: 16px;
  font-weight: 500;
  color: var(--ink-3);
  letter-spacing: 0;
}

/* Sub text */
.kpi-sub {
  font-size: 12.5px;
  color: var(--ink-3);
  margin-bottom: 6px;
}

/* Sparkline */
.kpi-sparkline {
  margin-top: auto;
  height: 56px;
  margin-left: -20px;
  margin-right: -20px;
}
.kpi-card--large .kpi-sparkline {
  height: 80px;
}
.kpi-sparkline svg {
  width: 100%;
  height: 100%;
  display: block;
}

/* Sparkline hover */
.spark-wrap {
  position: relative;
}
.spark-wrap svg {
  cursor: crosshair;
}
.spark-tip {
  position: absolute;
  bottom: calc(100% + 4px);
  transform: translateX(-50%);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-ctrl, 6px);
  padding: 5px 9px;
  pointer-events: none;
  white-space: nowrap;
  font-size: 11.5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  box-shadow: var(--shadow-md);
  z-index: 20;
}
.tip-date {
  color: var(--ink-3);
  font-size: 11px;
}
.tip-val {
  font-weight: 650;
  color: var(--ink);
}

/* Notices */
.notices-header {
  display: flex;
  align-items: center;
  margin: 28px 0 12px;
}
.notices-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.notices-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.notice-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-left: 3px solid #6366f1;
  border-radius: var(--r-card);
  padding: 16px 20px;
}
.notice-card--top  { border-left-color: #1db87a; }
.notice-card--mid  { border-left-color: #6366f1; }
.notice-card--low  { border-left-color: #818cf8; }

.notice-badge {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1.5px solid currentColor;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  margin-top: 1px;
}
.notice-badge--top {
  background: #1db87a;
  border-color: #1db87a;
  color: #fff;
}

.notice-body {
  flex: 1;
  min-width: 0;
}
.notice-desc {
  font-size: 13.5px;
  color: var(--ink);
  margin: 0 0 5px;
  line-height: 1.55;
}
.notice-cta {
  font-size: 12.5px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}
a.notice-cta:hover { text-decoration: underline; }

.notices-low-label {
  font-size: 12px;
  color: var(--ink-3);
  padding: 6px 0 2px;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: .5; }
}

/* Skeleton pulse */
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: .5; }
}

/* Responsive */
@media (max-width: 900px) {
  .kpi-grid {
    grid-template-columns: 1fr 1fr;
  }
  .kpi-card--large {
    grid-column: span 2;
    grid-row: span 1;
  }
}
@media (max-width: 560px) {
  .kpi-grid { grid-template-columns: 1fr; }
  .kpi-card--large { grid-column: span 1; }
}

@media (max-width: 640px) {
  .custom-range-drop {
    position: static;
    width: 100%;
    margin-bottom: 16px;
    display: grid;
    grid-template-columns: 44px 1fr;
    align-items: center;
    row-gap: 12px;
    column-gap: 10px;
  }
  .crd-arrow { display: none; }
  .crd-input { width: 100%; }
  .custom-range-drop .btn-primary {
    grid-column: 1 / -1;
    width: 100%;
    margin-top: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>